import type { Canvas } from 'fabric/fabric-impl';
import { RefObject } from 'react';
import { CustomFabricImage } from '../../../global-types';
import { calculateFabricCoordsCenter } from './calculate-fabric-coords-center';
import { checkIfImageIntersectsPhotozoneEdge } from './check-if-image-intersects-edge';
import { shrinkPt } from './shrink-pt';

/**
 * Arguments for applying force fill constraints to an image.
 */
export type ApplyImageForceFillConstraintsArguments = {
  canvas: RefObject<Canvas> | undefined;
  image: CustomFabricImage;
  isFront: boolean;
  scale: number;
};

/**
 * Determines if force fill constraints should be applied to the image.
 * @param photoZoneID - The ID of the photo zone.
 * @param isFront - Whether the image is on the front side.
 * @returns {boolean} True if constraints should be applied, false otherwise.
 */
export const shouldApplyForceFillConstraints = ({
  photoZoneId,
  isFront,
}: {
  photoZoneId: string | undefined;
  isFront: boolean;
}): boolean => Boolean(photoZoneId && isFront);

/**
 * Finds the photo zone object on the canvas by its ID.
 * @param canvas - Ref to the fabric canvas.
 * @param photoZoneId - The ID of the photo zone to find.
 * @returns The photo zone object if found, otherwise undefined.
 */
export const findPhotoZoneById = ({
  canvas,
  photoZoneId,
}: {
  canvas: RefObject<Canvas> | undefined;
  photoZoneId: string | undefined;
}) => canvas?.current?.getObjects().find((obj) => obj.name === photoZoneId);

/**
 * Resets the image position to the center of its associated photo zone.
 * @param image - The fabric image object.
 * @param canvas - The canvas ref.
 */
export const resetImagePositionToPhotoZoneCenter = ({
  image,
  canvas,
}: {
  image: CustomFabricImage;
  canvas: RefObject<Canvas> | undefined;
}): void => {
  const photoZoneId = image?.data?.photoZoneId;
  const photoZoneObject = findPhotoZoneById({ canvas, photoZoneId });
  const photoZoneCenter = photoZoneObject?.getCenterPoint();
  if (photoZoneCenter) {
    image.setPositionByOrigin(photoZoneCenter, 'center', 'center');
  }
};

/**
 * Applies force fill constraints to an image on the canvas, ensuring it stays within the photo zone.
 * If the image is scaled down and any edge intersects the photo zone edge, resets its position.
 *
 * @param canvas - Ref to the fabric canvas.
 * @param image - The custom fabric image.
 * @param isFront - Whether the image is on the front side.
 * @param scale - The current scale value.
 */
export const applyImageForceFillConstraints = ({
  canvas,
  image,
  isFront,
  scale,
}: ApplyImageForceFillConstraintsArguments): void => {
  const photoZoneId: string | undefined = image?.data?.photoZoneId;
  if (!shouldApplyForceFillConstraints({ photoZoneId, isFront })) return;

  const photoZoneObject = findPhotoZoneById({ canvas, photoZoneId });
  if (!photoZoneObject) return;

  const imageCoords = image?.getCoords();
  const photoZoneCoords = photoZoneObject?.getCoords();
  const center = photoZoneCoords ? calculateFabricCoordsCenter(photoZoneCoords) : { x: 0, y: 0 };

  const photoZoneMarginCoords = (photoZoneCoords as Array<{ x: number; y: number }>)?.map((coord) =>
    shrinkPt({ center, pt: coord }),
  );

  // Check if any image edge intersects with any photozone edge (with margin)
  const anyEdgeIntersect = checkIfImageIntersectsPhotozoneEdge({
    imageCoords,
    photoZoneMarginCoords: photoZoneMarginCoords as [fabric.Point, fabric.Point, fabric.Point, fabric.Point],
  });

  // If any corner is inside or any edge intersects, revert to last valid position
  if (image.data.scaleValue > scale && anyEdgeIntersect) {
    resetImagePositionToPhotoZoneCenter({ image, canvas });
  }
};
